import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings2, Info, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EmployeeWorkflowAssignment } from '@/types/workflow-assignment';
import {
  getWorkflowType,
  getWorkflowIcon,
  getWorkflowBadgeColor,
  getWorkflowCardColor,
} from '@/services/workflowAssignmentService';

interface WorkflowAssignmentCardProps {
  assignment: EmployeeWorkflowAssignment | null;
  isLoading: boolean;
  onChangeWorkflow: () => void;
  onRevertToDefault?: () => void;
  employeeName?: string;
}

export const WorkflowAssignmentCard: React.FC<WorkflowAssignmentCardProps> = ({
  assignment,
  isLoading,
  onChangeWorkflow,
  onRevertToDefault,
  employeeName,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-10 w-32" />
      </div>
    );
  }

  if (!assignment) {
    return (
      <Alert>
        <AlertDescription>
          Unable to load workflow assignment
        </AlertDescription>
      </Alert>
    );
  }

  const workflowType = getWorkflowType(assignment);
  const icon = getWorkflowIcon(workflowType);
  const badgeColor = getWorkflowBadgeColor(workflowType);
  const cardColor = getWorkflowCardColor(workflowType);

  const getWorkflowTitle = () => {
    switch (workflowType) {
      case 'default':
        return 'Use Organisation Default';
      case 'custom':
        return 'Custom Workflow Assigned';
      case 'no-approval':
        return 'No Authorisation Required';
    }
  };

  const getWorkflowDescription = () => {
    if (workflowType === 'no-approval') {
      return 'Timesheets automatically authorised upon submission';
    }
    return assignment.workflow?.description || '';
  };

  return (
    <div className="space-y-4">
      {/* Workflow Card */}
      <div className={cn('border rounded-lg p-4 space-y-3', cardColor)}>
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{icon}</span>
            <div>
              <Badge variant="outline" className={cn('font-medium', badgeColor)}>
                {getWorkflowTitle()}
              </Badge>
            </div>
          </div>
        </div>

        {/* Workflow Name */}
        {assignment.workflow && (
          <div>
            <h4 className="text-base font-medium text-foreground">
              {assignment.workflow.name}
            </h4>
            {getWorkflowDescription() && (
              <p className="text-sm text-muted-foreground mt-1">
                {getWorkflowDescription()}
              </p>
            )}
          </div>
        )}

        {/* Workflow Stats */}
        {assignment.workflow && workflowType !== 'no-approval' && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{assignment.workflow.totalStages} stages</span>
            <span>•</span>
            <span>{assignment.workflow.totalApprovers} approvers</span>
          </div>
        )}

        {/* No Authorisation Badge */}
        {workflowType === 'no-approval' && (
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-green-700">⚡ Auto-Authorisation Enabled</span>
          </div>
        )}

        {/* Assignment Info */}
        {!assignment.isDefault && assignment.assignedByName && assignment.assignedAt && (
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3" />
              Assigned by {assignment.assignedByName} on{' '}
              {new Date(assignment.assignedAt).toLocaleDateString()}
            </p>
            {assignment.reason && (
              <p className="text-xs text-muted-foreground mt-1 italic">
                Reason: {assignment.reason}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onChangeWorkflow}
          className="flex items-center gap-2"
        >
          <Settings2 className="h-4 w-4" />
          Change Workflow
        </Button>

        {!assignment.isDefault && onRevertToDefault && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRevertToDefault}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Revert to Default
          </Button>
        )}
      </div>
    </div>
  );
};
